import * as Sentry from '@sentry/react';
import { DraftOwnerMismatchError, type DraftScope } from './draftOwnership';
import { decompressSessionStorage } from './sessionStorage';

type ReadableStore = Pick<Storage, 'getItem'>;

/**
 * La copia de un formulario ya parseada, solo si NO dice ser de otra entidad.
 *
 * Tras `claimDraftScope` la copia ya es de la URL (una ajena o huérfana se
 * descartó); esto es el cinturón y es ESTRICTO: ilegible → `null`, con `id` de
 * otra entidad → `null` y sin `id` → `null` (manda el servidor; una copia que
 * no dice de quién es no tiene dueño comprobable, como en
 * `readOwnCommunicationEditCopy`). Sin entidad (URL sin `:id`) → `null`, salvo `allowWithoutEntity`
 * (pasos sin origen que hoy leen la copia tal cual). Nunca lanza.
 */
export const readOwnDraftCopy = (
  key: string,
  entityId: string | undefined,
  store: ReadableStore = sessionStorage,
  opts: { allowWithoutEntity?: boolean } = {}
): Record<string, unknown> | null => {
  if (!entityId && !opts.allowWithoutEntity) return null;
  let raw: string | null;
  try {
    raw = store.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const copy = JSON.parse(decompressSessionStorage(raw) ?? 'null');
    if (!copy || typeof copy !== 'object' || Array.isArray(copy)) return null;
    return !entityId || copy.id === entityId ? copy : null;
  } catch {
    return null;
  }
};

/** El `id` que dice llevar la copia `key`, o `undefined` (no hay, ilegible o sin id). Nunca lanza. */
export const readDraftCopyId = (
  key: string,
  store: ReadableStore = sessionStorage
): string | undefined => {
  let raw: string | null;
  try {
    raw = store.getItem(key);
  } catch {
    return undefined;
  }
  if (!raw) return undefined;
  try {
    const copy = JSON.parse(decompressSessionStorage(raw) ?? 'null');
    return copy && typeof copy.id === 'string' && copy.id ? copy.id : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Falla CERRADO si la copia dice ser de `entityId` y el ámbito no tiene a esa
 * entidad como dueño (sin dueño o con otro). `assertDraftOwner` por sí solo
 * deja pasar la ausencia de dueño; aquí, con una copia identificada, no.
 */
export const requireDraftOwner = (
  scope: DraftScope,
  entityId: string | undefined,
  store: ReadableStore = sessionStorage
): void => {
  if (!entityId) return;
  const owner = store.getItem(scope.ownerKey);
  if (owner === entityId) return;
  const error = new DraftOwnerMismatchError(scope.name, owner, entityId);
  Sentry.captureException(error, {
    tags: { guard: `draft-owner-mismatch:${scope.name}` },
  });
  throw error;
};

/**
 * Barrera del guardado desde la COPIA `key` (falla CERRADO). Sin copia, nada
 * que comprobar. Con copia: si hay dueño, el `id` de la copia tiene que ser
 * ese dueño — una copia sin `id` o con otro lanza —; si no hay dueño, solo
 * pasa una copia que tampoco dice de quién es (el paso sin origen, que no
 * reclama nada).
 */
export const assertCopyBelongsToOwner = (
  scope: DraftScope,
  key: string,
  store: ReadableStore = sessionStorage
): void => {
  let raw: string | null;
  try {
    raw = store.getItem(key);
  } catch {
    return;
  }
  if (!raw) return;
  const owner = store.getItem(scope.ownerKey);
  const copyId = readDraftCopyId(key, store);
  if (!owner && !copyId) return;
  if (owner === copyId) return;
  const error = new DraftOwnerMismatchError(scope.name, owner, copyId ?? '(copia sin id)');
  Sentry.captureException(error, {
    tags: { guard: `draft-owner-mismatch:${scope.name}` },
  });
  throw error;
};
